import {
  Create,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  PasswordInput,
  required,
  email,
  minLength,
  maxLength,
  usePermissions,
  useRecordContext,
  TopToolbar,
  ListButton,
} from 'react-admin';
import { Alert, Box } from '@mui/material';
import { assignableRolesFor, MIN_PASSWORD_LENGTH } from './role';
import { useFeature } from '../featureFlags/FeatureGate';
import { FEATURE_INTAKE_DEPARTMENTS } from '../intake/intakeSides';
import { PermissionGrantsSection } from './PermissionGrantsSection';
import { useCustomRoles } from '../roleCenter/customRoles';
import { ImpersonateButton } from './ImpersonateButton';

/**
 * Najdłuższe imię i nazwisko — jak `MAX_PERSON_NAME_LENGTH` w API
 * (`users/users.service.ts`, kolumny `VARCHAR(100)`). Rozstrzyga serwer;
 * formularz tylko nie przepuszcza tego, co i tak zostanie odrzucone.
 */
export const MAX_PERSON_NAME_LENGTH = 100;

/**
 * Spółka konta do komunikatu — nazwą, nie numerem (07.10.2026, lista
 * komunikatów z 01.10: „formularz konta pokazuje numer spółki zamiast
 * nazwy”). API dołącza do konta jawne pola spółki (`COMPANY_PUBLIC_RELATION`);
 * numer zostaje tylko wtedy, gdy nazwy w odpowiedzi nie ma. `null` = konto
 * bez spółki.
 */
export function accountCompanyText(
  record: Record<string, unknown> | undefined,
): string | null {
  if (record?.companyId == null) return null;
  const name = (record.company as { name?: unknown } | null | undefined)?.name;
  return typeof name === 'string' && name.trim() !== ''
    ? name
    : `nr ${String(record.companyId)}`;
}

/**
 * Formularz konta.
 *
 * Wspólny dla zakładania i zmiany, bo pola są te same — różni je wyłącznie
 * to, czy hasło jest wymagane. Przy zmianie puste hasło znaczy „zostaw
 * dotychczasowe", a nie „wyczyść".
 */
const UserFields = ({ isNew }: { isNew: boolean }) => {
  const { permissions } = usePermissions<string>();
  const record = useRecordContext();
  // Role CS i Administracja (fala B, 21.10.2026) — tylko z włączonymi
  // działami przyjęcia zleceń; rola, którą konto już ma, zostaje.
  const intakeDepartmentsOn = useFeature(FEATURE_INTAKE_DEPARTMENTS);
  // Role własne z centrum ról (22.10.2026, `roleCenter`) — z API, tylko te,
  // które zalogowany może nadać; bez centrum lista jak dotąd.
  const customRoles = useCustomRoles();
  const roleChoices = [
    ...assignableRolesFor(
      permissions,
      intakeDepartmentsOn,
      (record?.role as string | undefined) ?? null,
    ),
    ...customRoles.map((r) => ({ id: r.id, name: r.name })),
  ];
  const company = accountCompanyText(record);

  return (
    <>
      {!isNew && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Puste pole hasła zostawia dotychczasowe. Wpisanie nowego od razu
          unieważnia link do resetu, jeśli taki był wysłany.
        </Alert>
      )}
      {/* Imię i nazwisko (07.10.2026) — podpis zapytań do przewoźników.
          Opcjonalne: puste pole API zapisuje jako brak, a podpis dostaje
          wtedy znacznik do uzupełnienia. */}
      <TextInput
        source="firstName"
        label="Imię"
        validate={maxLength(MAX_PERSON_NAME_LENGTH)}
        helperText="Do podpisu zapytań do przewoźników"
      />
      <TextInput
        source="lastName"
        label="Nazwisko"
        validate={maxLength(MAX_PERSON_NAME_LENGTH)}
      />
      <TextInput source="username" label="Nazwa użytkownika" validate={required()} />
      <TextInput source="email" label="Adres e-mail" validate={[required(), email()]} />
      <PasswordInput
        source="password"
        label={isNew ? 'Hasło' : 'Nowe hasło (opcjonalnie)'}
        validate={
          isNew
            ? [required(), minLength(MIN_PASSWORD_LENGTH)]
            : [minLength(MIN_PASSWORD_LENGTH)]
        }
        helperText={`Co najmniej ${MIN_PASSWORD_LENGTH} znaków`}
      />
      <SelectInput
        source="role"
        label="Rola"
        choices={roleChoices}
        validate={required()}
        helperText={
          roleChoices.length === 0
            ? 'Twoja rola nie pozwala nadawać ról'
            : undefined
        }
      />
      <TextInput source="phone" label="Telefon" />
      {/* Identyfikatora spółki nie wystawiamy jako pola do wpisania: przy
          zakładaniu konta ustala ją API na podstawie uprawnień osoby
          zakładającej. Pokazywanie go zapraszałoby do wpisania cudzej. */}
      {!isNew && company !== null && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Konto należy do spółki {company}. Przypisanie zmienia administrator
          globalny.
        </Alert>
      )}
    </>
  );
};

export const UserCreate = () => (
  <Create title="Nowe konto" redirect="list">
    <SimpleForm>
      <UserFields isNew />
    </SimpleForm>
  </Create>
);

/** Pasek karty konta — „Zaloguj jako” (02.11.2026) obok powrotu do listy. */
const UserEditActions = () => (
  <TopToolbar>
    <ImpersonateButton variant="toolbar" />
    <ListButton label="Lista" />
  </TopToolbar>
);

export const UserEdit = () => (
  <Edit title="Konto" redirect="list" mutationMode="pessimistic" actions={<UserEditActions />}>
    <SimpleForm>
      <UserFields isNew={false} />
    </SimpleForm>
    {/* Uprawnienia imienne (fala B, 21.10.2026) — zapis osobno, z powodem. */}
    <Box sx={{ px: 2, pb: 2 }}>
      <PermissionGrantsSection />
    </Box>
  </Edit>
);
